import { useState, useEffect, useMemo } from "react";

import { Toast } from "@newsela/angelou";
import { inRange } from "@s-libs/micro-dash";

import { READ_ALOUD_SPEEDS } from "@/view/constants/readAloudSpeeds/readAloudSpeeds";
import globalToasterRef from "@/base/globalToasterRef";
import useReadAloudMode from "../useReadAloudMode";
import { toastCss } from "./useReadAloudAudio.style";

import getCurrentSpan from "./utils/getCurrentSpan";
import getTextChunks from "./utils/getTextChunks";
import isElementWithinInertText from "./utils/isElementWithinInertText";
import processSpeechMarks from "./utils/processSpeechMarks";
import requestAudioFile from "./utils/requestAudioFile";
import setActiveSpan from "./utils/setActiveSpan";

/**
 * useReadAloudAudio breaks text content into small chunks and requests and returns audio files for each chunk.
 * It requests the first two files immediately. Then, as each file is played, it will request the subsequent chunk.
 *
 * This relies on an audio element with the id '#read-aloud-audio' being present on the page, as in ReadAloudAudio.jsx.
 *
 * @param {Object} content
 * @param {Object} selectedLevel
 *
 */
const useReadAloudAudio = ({ content, selectedLevel, text }) => {
  const initialAudioState = {
    currentTextChunkIndex: 0,
    isPlaying: false,
    playbackRate: READ_ALOUD_SPEEDS["100%"],
    speechMarks: [],
    vocalFiles: [],
    text,
  };

  const [audioState, setAudioState] = useState(initialAudioState);
  const [errorState, setErrorState] = useState();
  const [loadingState, setLoadingState] = useState();
  const [currentTime, setCurrentTime] = useState(null);
  const [shouldSyncTimeAfterRateChange, setShouldSyncTimeAfterRateChange] =
    useState(false);
  const [currentPlaybackRate, setCurrentPlaybackRate] = useState(
    READ_ALOUD_SPEEDS["100%"],
  );

  // The speech mark for the current word being read.
  const [currentSpeechMark, setCurrentSpeechMark] = useState(null);
  // An array of speech marks matching the current text chunk.
  const [currentSpeechMarks, setCurrentSpeechMarks] = useState([]);
  const [isPausedForBlurredText, setIsPausedForBlurredText] = useState(false);
  const {
    readAloudSpanRefs,
    isReadAloudModeActive,
    currentMark,
    setCurrentMark,
  } = useReadAloudMode();

  const textChunks = useMemo(
    () => getTextChunks(content, selectedLevel, text),
    [content, selectedLevel, text],
  );

  // Used to play and pause audio.
  const toggleAudio = () => {
    setAudioState((prevState) => ({
      ...prevState,
      isPlaying: !prevState.isPlaying,
    }));
  };

  const pauseAudio = () => {
    setAudioState((prevState) => ({
      ...prevState,
      isPlaying: false,
    }));
  };

  const playAudio = () => {
    setAudioState((prevState) => ({
      ...prevState,
      isPlaying: true,
    }));
  };

  // Requests audio files and adds vocal files and speech marks to state.
  useEffect(() => {
    const requestAudioFilesAndProcessSpeech = async () => {
      let vocalFiles = [...audioState.vocalFiles];
      let speechMarks = [...audioState.speechMarks];

      if (!textChunks[0]) return null;

      // If necessary, generate audio for the first chunk of text.
      if (!vocalFiles.length) {
        const { data, error, loading } = await requestAudioFile(
          textChunks[0]?.text,
          selectedLevel?.language,
          currentPlaybackRate.value,
        );

        // Update speech marks to reflect character count rather than byte size.
        const initialProcessedSpeechMarks = processSpeechMarks(
          data?.speech_marks,
          textChunks[0]?.text,
          textChunks[0]?.offset,
        );

        speechMarks = initialProcessedSpeechMarks
          ? [...speechMarks, initialProcessedSpeechMarks]
          : speechMarks;
        vocalFiles = data ? [data.download_url] : vocalFiles;

        setCurrentSpeechMarks(initialProcessedSpeechMarks);
        setErrorState(error);
        setLoadingState(loading);
      }

      const additionalAudioFileNeeded =
        audioState.currentTextChunkIndex + 1 !== textChunks.length;
      const nextTextChunkIndex = !additionalAudioFileNeeded
        ? audioState.currentTextChunkIndex
        : audioState.currentTextChunkIndex + 1;

      // When the playback rate has changed, the vocal files for the current and following text chunks need to be re-requested at the new speed.
      if (audioState.playbackRate.value !== currentPlaybackRate.value) {
        pauseAudio(); // stops other useEffects from triggering and decreases race conditions

        const { currentTextChunkIndex } = audioState;
        const newVocalFiles = [];
        const newSpeechMarks = [];

        // Fetch audio files for new read aloud rate for the current vocalFiles.
        const {
          data: currentAudioData,
          error,
          loading,
        } = await requestAudioFile(
          textChunks[currentTextChunkIndex]?.text,
          selectedLevel?.language,
          currentPlaybackRate.value,
        );

        // Update the indexes in the vocal files array with the new files.
        newVocalFiles[currentTextChunkIndex] = currentAudioData.download_url;

        // Get processed speech marks for currently playing audio file.
        const newCurrentProcessedSpeechMarks = processSpeechMarks(
          currentAudioData?.speech_marks,
          textChunks[currentTextChunkIndex]?.text,
          textChunks[currentTextChunkIndex]?.offset,
        );

        // Set speech marks to correct index
        newSpeechMarks[currentTextChunkIndex] = newCurrentProcessedSpeechMarks;

        if (additionalAudioFileNeeded) {
          // Fetch audio files for new read aloud rate for the next vocalFiles.
          const { data: nextAudioData } = await requestAudioFile(
            textChunks[nextTextChunkIndex]?.text,
            selectedLevel?.language,
            currentPlaybackRate.value,
          );
          newVocalFiles[nextTextChunkIndex] = nextAudioData.download_url;
          // Get processed speech marks for the next audio file.
          const nextProcessedSpeechMarks = processSpeechMarks(
            nextAudioData?.speech_marks,
            textChunks[nextTextChunkIndex]?.text,
            textChunks[nextTextChunkIndex]?.offset,
          );
          newSpeechMarks[nextTextChunkIndex] = nextProcessedSpeechMarks;
        }

        // Get start position for current speech mark. This needs to be done before currentSpeechMark is updated
        const currentStartPosition = currentSpeechMark?.start;

        // Get index for new speech mark that matches existing start position.
        const newCurrentSpeechMarkIndex =
          newCurrentProcessedSpeechMarks.findIndex(
            (mark) => mark.start === currentStartPosition,
          );
        const newCurrentSpeechMark =
          newCurrentProcessedSpeechMarks[newCurrentSpeechMarkIndex];
        // Remove speech marks that have already been spoken.
        const remainingSpeechMarks =
          newCurrentSpeechMarkIndex === -1
            ? newCurrentProcessedSpeechMarks
            : newCurrentProcessedSpeechMarks.slice(
                newCurrentSpeechMarkIndex + 1,
              );

        const nextStartingCurrentTime = newCurrentSpeechMark?.time;

        setCurrentSpeechMark(newCurrentSpeechMark);
        setCurrentSpeechMarks(remainingSpeechMarks);
        setErrorState(error);
        setLoadingState(loading);
        setCurrentTime(nextStartingCurrentTime);
        setAudioState((prevState) => ({
          ...prevState,
          playbackRate: currentPlaybackRate,
          vocalFiles: newVocalFiles,
          speechMarks: newSpeechMarks,
          isRateChange: true,
          currentTime,
        }));
        setShouldSyncTimeAfterRateChange(true);
        // Return out of the useEffect early to avoid duplicate speech marks and vocal files.
        return null;
      }

      // There are no remaining sections to generate.
      if (textChunks.length <= nextTextChunkIndex) return null;

      // When we change rates and jump back, there could be some undefined vocalFiles
      if (
        !!audioState.vocalFiles.length &&
        !audioState.vocalFiles[audioState.currentTextChunkIndex]
      ) {
        const { data: currentAudioData } = await requestAudioFile(
          textChunks[audioState.currentTextChunkIndex]?.text,
          selectedLevel?.language,
          currentPlaybackRate.value,
        );
        const speechMarks = processSpeechMarks(
          currentAudioData?.speech_marks,
          textChunks[audioState.currentTextChunkIndex]?.text,
          textChunks[audioState.currentTextChunkIndex]?.offset,
        );

        const allVocalFiles = [...audioState.vocalFiles];
        const allSpeechMarks = [...audioState.speechMarks];

        allVocalFiles[audioState.currentTextChunkIndex] =
          currentAudioData.download_url;
        allSpeechMarks[audioState.currentTextChunkIndex] = speechMarks;

        setCurrentSpeechMarks(speechMarks);
        setAudioState((prevState) => ({
          ...prevState,
          vocalFiles: allVocalFiles,
          speechMarks: allSpeechMarks,
        }));

        return null;
      }

      // The audio file already exists. No need to generate.
      if (audioState.vocalFiles[nextTextChunkIndex]) return null;

      // Generate audio for the upcoming chunk of text.
      const { data, error, loading } = await requestAudioFile(
        textChunks[nextTextChunkIndex]?.text,
        selectedLevel?.language,
        currentPlaybackRate.value,
      );

      // Update speech marks to reflect character count rather than byte size.
      const nextProcessedSpeechMarks = processSpeechMarks(
        data?.speech_marks,
        textChunks[nextTextChunkIndex]?.text,
        textChunks[nextTextChunkIndex]?.offset,
      );

      speechMarks = nextProcessedSpeechMarks
        ? [...speechMarks, nextProcessedSpeechMarks]
        : speechMarks;
      vocalFiles = data ? [...vocalFiles, data.download_url] : vocalFiles;

      setErrorState(error);
      setLoadingState(loading);
      setAudioState((prevState) => ({
        ...prevState,
        vocalFiles,
        speechMarks,
        playbackRate: currentPlaybackRate,
      }));
    };
    requestAudioFilesAndProcessSpeech();
  }, [
    audioState.currentTextChunkIndex,
    JSON.stringify(textChunks),
    isReadAloudModeActive,
    JSON.stringify(currentPlaybackRate),
    JSON.stringify(audioState.vocalFiles),
  ]);

  // Updates the current speech marks to reflect the current text chunk.
  useEffect(() => {
    setCurrentSpeechMarks(
      audioState.speechMarks?.[audioState.currentTextChunkIndex] || [],
    );
  }, [audioState.currentTextChunkIndex]);

  // Updates the current speech mark based on the word being read.
  useEffect(() => {
    let newCurrentSpeechMark;
    const currentTimeInteger = Math.floor(currentTime);
    const isLastSpeechMarkInChunk = !currentSpeechMarks?.[1];
    const isMarkInRange = isLastSpeechMarkInChunk
      ? currentTimeInteger > currentSpeechMarks?.[0]?.time
      : inRange(
          currentTimeInteger,
          currentSpeechMarks?.[0]?.time,
          currentSpeechMarks?.[1]?.time,
        );

    if (isMarkInRange) {
      newCurrentSpeechMark = currentSpeechMarks[0];
      setCurrentSpeechMark(newCurrentSpeechMark);
      const newSpeechMarks = [...currentSpeechMarks];
      // Remove current element.
      // This allows us to only check the first speech mark to see if it matches the new time,
      // rather than iterating through the full array of speech marks.
      newSpeechMarks.shift();
      setCurrentSpeechMarks(newSpeechMarks);
    }
  }, [audioState.currentTextChunkIndex, currentTime]);

  const currentSpan = getCurrentSpan({
    audioState,
    currentSpeechMark,
    readAloudSpanRefs,
    textChunks,
  });

  const isCurrentSpanBlurred = isElementWithinInertText(currentSpan);

  useEffect(() => {
    if (isCurrentSpanBlurred) setIsPausedForBlurredText(isCurrentSpanBlurred);
  }, [isCurrentSpanBlurred]);

  useEffect(() => {
    if (isPausedForBlurredText) {
      pauseAudio();
      globalToasterRef.current.displayToast({
        children:
          "Exit listen mode and complete the checks for understanding quiz to continue.",
        statusColor: Toast.statusColor.warning,
        showIcon: true,
        __cssFor: {
          root: toastCss,
        },
      });
    }
  }, [isPausedForBlurredText]);

  // Adds and removes styles based on the current span.
  useEffect(() => {
    const previousCurrentSpan = document.querySelector('[data-current="true"]');
    if (isPausedForBlurredText) return setActiveSpan(currentSpan, false);
    setActiveSpan(previousCurrentSpan, false);
    setActiveSpan(currentSpan, true);
    setCurrentMark && setCurrentMark(currentSpeechMark);
  }, [currentSpeechMark, isPausedForBlurredText]);

  const swapToNextAudioFile = () => {
    setAudioState((prevState) => {
      const nextTextChunkIndex = prevState.currentTextChunkIndex + 1;

      // If we've reached the end of the text, reset audio to beginning.
      if (textChunks.length <= nextTextChunkIndex) {
        return { ...prevState, isPlaying: false, currentTextChunkIndex: 0 };
      }

      // Otherwise, set the next audio file for playback.
      return { ...prevState, currentTextChunkIndex: nextTextChunkIndex };
    });
  };

  // We're avoiding reseting the audio state when Read Aloud mode is toggled off
  // to prevent users from having to restart from the beginning, particularly while
  // answering checks for understanding questions
  useEffect(() => {
    if (!isReadAloudModeActive) {
      const speechMarks =
        audioState.speechMarks[audioState.currentTextChunkIndex];
      setCurrentSpeechMarks(speechMarks);
      setIsPausedForBlurredText(false);
    }
  }, [isReadAloudModeActive]);

  // Reset the audio state when the article level is changed
  useEffect(() => {
    setAudioState((prevState) => ({
      ...initialAudioState,
      playbackRate: prevState.playbackRate,
    }));
    setIsPausedForBlurredText(false);
  }, [selectedLevel?.id]);

  const resetAudio = () => {
    setIsPausedForBlurredText(false);
    setAudioState(() => ({
      ...initialAudioState,
      playbackRate: currentPlaybackRate,
    }));
    const audio = document.getElementById("read-aloud-audio");
    audio.currentTime = 0;
  };

  // Reset the text and resume playing; useful in cases where there is only one text chunk
  const restartAudio = () => {
    setAudioState((prevState) => ({
      ...prevState,
      currentTextChunkIndex: 0,
      isPlaying: true,
    }));

    const speechMarks =
      audioState.speechMarks[audioState.currentTextChunkIndex];
    setCurrentSpeechMarks(speechMarks);
  };

  const jumpBack = () => {
    setIsPausedForBlurredText(false);
    const JUMP_BACK_THRESHOLD_MS = 3000;

    if (
      currentSpeechMark.time <= JUMP_BACK_THRESHOLD_MS &&
      audioState.currentTextChunkIndex > 0
    ) {
      setAudioState((prevState) => ({
        ...prevState,
        currentTextChunkIndex: prevState.currentTextChunkIndex - 1,
      }));
    } else {
      setCurrentTime(0);
      setShouldSyncTimeAfterRateChange(true);

      const speechMarks =
        audioState.speechMarks[audioState.currentTextChunkIndex];
      setCurrentSpeechMarks(speechMarks);
    }
  };

  return {
    currentPlaybackRate,
    currentTextChunkIndex: audioState.currentTextChunkIndex,
    currentTime,
    error: errorState,
    isLoading: loadingState,
    isPlaying: audioState.isPlaying,
    isPausedForBlurredText,
    jumpBack,
    shouldSyncTimeAfterRateChange,
    pauseAudio,
    playAudio,
    resetAudio,
    restartAudio,
    audioState,
    setAudioState,
    setCurrentPlaybackRate,
    setCurrentTime,
    setIsPausedForBlurredText,
    setShouldSyncTimeAfterRateChange,
    swapToNextAudioFile,
    textChunks,
    toggleAudio,
    vocalFiles: audioState.vocalFiles,
    currentMark,
  };
};

export default useReadAloudAudio;
